import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { ValueLabelRecord } from '@blisspointmedia/bpm-types/dist/App2Performance';
import {
  BenchmarkingData,
  BenchmarkingQueryMetaData
} from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { App2Channels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton/GraphTableToggleButton';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  CAPITALIZED_DATE_PERIODS_MAP,
  DATE_PERIODS_TO_NOUN,
  MORE_INFO_COPY,
  transformData
} from './benchmarkingConstants';
import { BenchmarkingSkeleton } from './BenchmarkingSkeleton';
import { ChannelBenchmarkingChart, TransformedData } from './ChannelBenchmarkingChart';

interface ChannelBenchmarkingProps {
  company: string;
  channel: App2Channels;
  data: Partial<BenchmarkingData> | undefined;
  activeMetaData?: BenchmarkingQueryMetaData;
  activePlatformOptions: ValueLabelRecord[] | undefined;
  selectedPlatform: string;
  setSelectedPlatform: Dispatch<SetStateAction<string>>;
  verticalOptions: ValueLabelRecord[];
  selectedVertical: string;
  setSelectedVertical: Dispatch<SetStateAction<string>>;
  kpis: string[] | null;
}

export const ChannelBenchmarking = ({
  channel,
  data,
  company,
  activeMetaData,
  activePlatformOptions,
  selectedPlatform,
  setSelectedPlatform,
  verticalOptions,
  selectedVertical,
  setSelectedVertical,
  kpis
}: ChannelBenchmarkingProps) => {
  const { name } = useCompanyInfo(company);
  const urlCompany = useLocationCompany();

  const displayName = useMemo(
    () => (TEST_COMPANIES.includes(urlCompany || '') ? 'Client' : name),
    [name, urlCompany]
  );

  const datePeriodOptions = useMemo(() => {
    const periods = (Object.keys(data ?? {}) as (keyof BenchmarkingData)[]).filter(period => {
      const d = data?.[period];
      return d != null && Object.keys(d).length > 0;
    });
    return periods.map(v => ({ value: v, label: CAPITALIZED_DATE_PERIODS_MAP[v] }));
  }, [data]);

  const defaultDatePeriod = useMemo(() => {
    const keys = datePeriodOptions.map(o => o.value);
    return keys.includes('weekly') ? 'weekly' : keys[0];
  }, [datePeriodOptions]);

  const [selectedDatePeriod, setSelectedDatePeriod] = useState<string | undefined>(
    defaultDatePeriod
  );
  const [isGraphView, setIsGraphView] = useState(true);
  const [showPercentChange, setShowPercentChange] = useState(false);

  // Reset user-controlled state when data changes (during render, per React docs recommendation)
  const [prevData, setPrevData] = useState(data);
  if (prevData !== data) {
    setPrevData(data);
    setSelectedDatePeriod(defaultDatePeriod);
    setShowPercentChange(false);
    setIsGraphView(true);
  }

  const activeDatePeriod = selectedDatePeriod as keyof BenchmarkingData | undefined;

  const [chartData, tercileOptions] = useMemo(
    () =>
      activeDatePeriod && data?.[activeDatePeriod]
        ? [transformData(data[activeDatePeriod]), Object.keys(data[activeDatePeriod])]
        : [[], []],
    [data, activeDatePeriod]
  );

  const joinedChartData = useMemo(() => {
    const joinedData: TransformedData = [];
    chartData.forEach((item, i, d) => {
      const datum = {
        date: item.date,
        // keeping as project evolves, may be helpful to have comparison period label
        compDate: d[i - 1]?.date || null
      };
      for (const [key, value] of Object.entries(item)) {
        if (key === 'date') {
          continue;
        } else if (d[i - 1] && d[i - 1][key] && typeof value === 'number') {
          const compVal = d[i - 1][key] as number;
          datum[key] = (value - compVal) / compVal;
        } else {
          datum[key] = null;
        }
      }
      joinedData.push(datum);
    });

    return joinedData;
  }, [chartData]);

  return (
    <div className='channel-benchmarking'>
      <WidgetContainer
        header={
          <div className='widget-header-container'>
            <span className='header-title-span'>{`${displayName} vs `}</span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              options={verticalOptions}
              value={selectedVertical}
              onChange={setSelectedVertical}
              type={DropdownToggleType.PAGE_TITLE}
            />
            <span className='header-title-span'>Benchmarks</span>
            <InfoTooltip size={Size.SM}>
              <div className='x-client-benchmarking-more-info-tooltip'>
                {MORE_INFO_COPY.map((info, i) => (
                  <div className='more-info-item' key={`more-info-item-${i}`}>
                    {info}
                  </div>
                ))}
              </div>
            </InfoTooltip>
            <span className='header-title-span extra-padding'>for</span>
            {activePlatformOptions && (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                options={activePlatformOptions}
                onChange={setSelectedPlatform}
                value={selectedPlatform}
                type={DropdownToggleType.PAGE_TITLE}
              />
            )}
            <span className='header-title-span extra-padding'>|</span>
            {Object.keys(data ?? {}).length > 0 && (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                options={datePeriodOptions}
                onChange={setSelectedDatePeriod}
                value={selectedDatePeriod}
                type={DropdownToggleType.PAGE_TITLE}
              />
            )}
          </div>
        }
        rightActions={
          <>
            {selectedDatePeriod && (
              <ToggleSwitch
                label={`${DATE_PERIODS_TO_NOUN[selectedDatePeriod]} over ${DATE_PERIODS_TO_NOUN[selectedDatePeriod]}`}
                checked={showPercentChange}
                onChange={setShowPercentChange}
                disabled={!joinedChartData.length}
                size={Size.LG}
              />
            )}
            <GraphTableToggleButton
              graphView={isGraphView}
              setGraphView={setIsGraphView}
              widgetName='crossClientBenchmarking'
              disabled={!chartData.length}
            />
          </>
        }
        includeDivider
      >
        {renderContent({
          data,
          activeDatePeriod,
          activeMetaData,
          channel,
          chartData,
          joinedChartData,
          showPercentChange,
          isGraphView,
          name: displayName,
          tercileOptions,
          selectedDatePeriod,
          kpis
        })}
      </WidgetContainer>
    </div>
  );
};

interface ContentProps {
  data: Partial<BenchmarkingData> | undefined;
  activeDatePeriod: keyof BenchmarkingData | undefined;
  activeMetaData: BenchmarkingQueryMetaData | undefined;
  channel: App2Channels;
  chartData: ReturnType<typeof transformData>;
  joinedChartData: TransformedData;
  showPercentChange: boolean;
  isGraphView: boolean;
  name: string;
  tercileOptions: string[];
  selectedDatePeriod: string | undefined;
  kpis: string[] | null;
}

const renderContent = ({
  data,
  activeDatePeriod,
  activeMetaData,
  channel,
  chartData,
  joinedChartData,
  showPercentChange,
  isGraphView,
  name,
  tercileOptions,
  selectedDatePeriod,
  kpis
}: ContentProps) => {
  if (!data) {
    return <BenchmarkingSkeleton />;
  }

  if (Object.keys(data).length === 0) {
    return (
      <span className='no-data-message' data-testid='no-data-message'>
        No data with current filter settings, please adjust filters to refetch
      </span>
    );
  }

  if (activeDatePeriod && data[activeDatePeriod] && activeMetaData && selectedDatePeriod) {
    return (
      <>
        <ChannelBenchmarkingChart
          key='channelBenchmarkingChart1'
          channel={channel}
          chartData={showPercentChange ? joinedChartData : chartData}
          companyPrettyName={name}
          isGraphView={isGraphView}
          companyName={name}
          activeMetaData={activeMetaData}
          isPrimary
          showPercentChange={showPercentChange}
          tercileOptions={tercileOptions}
          selectedDatePeriod={selectedDatePeriod}
          kpis={kpis}
        />
        <ChannelBenchmarkingChart
          key='channelBenchmarkingChart2'
          channel={channel}
          chartData={showPercentChange ? joinedChartData : chartData}
          companyPrettyName={name}
          isGraphView={isGraphView}
          companyName={name}
          activeMetaData={activeMetaData}
          isPrimary={false}
          showPercentChange={showPercentChange}
          tercileOptions={tercileOptions}
          selectedDatePeriod={selectedDatePeriod}
          kpis={kpis}
        />
      </>
    );
  }

  return <BenchmarkingSkeleton />;
};
